import React from "react";
import Immutable from "immutable";

const DEFAULT_DURATION = 30;

const noop = () => {};

// Lets panels on the active tab follow the presentation clock (e.g. a leaderboard stepping
// through its leaders) and ask for the tab to stay up longer than its minimum duration.
export const inactivePresentationClock = Object.freeze({
  isAutoAdvancing: false,
  elapsedSeconds: 0,
  requestDuration: noop
});

export const PresentationClockContext = React.createContext(inactivePresentationClock);

export const getNextNonSkippedTabId = (sortedTabs, currentTabId) => {
  const eligible = sortedTabs.filter(t => !t.get("skipInPresentation", false));
  if (eligible.size === 0) return null;
  const currentIdx = eligible.findIndex(t => t.get("id") === currentTabId);
  const nextIdx = (currentIdx + 1) % eligible.size;
  return eligible.getIn([nextIdx, "id"]);
};

// The tab's configured duration is a *minimum*: a panel can extend it via `requestDuration`
// (see PresentationClockContext) when it needs longer to present everything it has.
//
// `isActiveTabLoaded` gates the clock: while the active tab is still loading (which
// includes performance-snapshot cells — see app.js load tracking) the clock is held
// so a tab is always shown fully rendered for its full duration and we never advance
// away from, or onto, a half-loaded tab.
const usePresentationTimer = (tabs, activeTabId, setActiveTabId, isPresenting, isActiveTabLoaded) => {
  // Paired with the tab it was counted on: after a manual tab change the first render still
  // holds the previous tab's count, which must read as zero rather than skip the new tab.
  const [elapsed, setElapsed] = React.useState({tabId: activeTabId, seconds: 0});
  const elapsedSeconds = elapsed.tabId === activeTabId ? elapsed.seconds : 0;
  const [requestedDurations, setRequestedDurations] = React.useState(Immutable.Map());

  const isActiveTabLoadedRef = React.useRef(isActiveTabLoaded);
  isActiveTabLoadedRef.current = isActiveTabLoaded;

  const sortedTabs = React.useMemo(() => tabs.sortBy(t => t.get("position")), [tabs]);
  const activeTab = sortedTabs.find(t => t.get("id") === activeTabId);
  const minimumDuration = activeTab
      ? activeTab.get("presentationDuration", DEFAULT_DURATION)
      : DEFAULT_DURATION;
  const duration = Math.max(minimumDuration, requestedDurations.max() || 0);

  React.useEffect(() => {
    setElapsed({tabId: activeTabId, seconds: 0});
    if (!isPresenting) {
      return;
    }

    const id = setInterval(() => {
      if (isActiveTabLoadedRef.current) {
        setElapsed(prev => ({tabId: activeTabId, seconds: prev.tabId === activeTabId ? prev.seconds + 1 : 1}));
      }
    }, 1000);

    return () => clearInterval(id);
  }, [isPresenting, activeTabId]);

  React.useEffect(() => {
    if (isPresenting && elapsedSeconds >= duration) {
      const nextId = getNextNonSkippedTabId(sortedTabs, activeTabId);
      if (nextId) setActiveTabId(nextId);
      // Reset explicitly: with a single eligible tab the active id does not change.
      setElapsed({tabId: activeTabId, seconds: 0});
    }
  }, [isPresenting, elapsedSeconds, duration, sortedTabs, activeTabId, setActiveTabId]);

  const requestDuration = React.useCallback((requesterId, seconds) => {
    setRequestedDurations(durations => seconds > 0
        ? durations.set(requesterId, seconds)
        : durations.delete(requesterId));
  }, []);

  const clock = React.useMemo(() => ({
    isAutoAdvancing: isPresenting,
    elapsedSeconds,
    requestDuration
  }), [isPresenting, elapsedSeconds, requestDuration]);

  return {
    secondsRemaining: isPresenting ? Math.max(duration - elapsedSeconds, 0) : null,
    clock
  };
};

export default usePresentationTimer;
